import * as THREE from 'three';
import { acceleratedRaycast, computeBoundsTree, disposeBoundsTree } from 'three-mesh-bvh';

THREE.BufferGeometry.prototype.computeBoundsTree = computeBoundsTree;
THREE.BufferGeometry.prototype.disposeBoundsTree = disposeBoundsTree;
THREE.Mesh.prototype.raycast = acceleratedRaycast;

export const RAFT_THICKNESS = 1.8;
/** Gap between the raft top and the model's lowest point. */
export const SUPPORT_GAP = 9;
export const MODEL_LIFT = RAFT_THICKNESS + SUPPORT_GAP;

const SPACING = 3.6;
const PILLAR_R = 0.42;
const BRACE_R = 0.22;
const FOOT_H = 1.4;
const BULGE_H = 0.9;
const TIP_H = 2.6;
const RAFT_MARGIN = 2.4;
/** A relocated touchpoint must stay on the same overhang (within this many mm in height). */
const EDGE_MAX_DY = 4;

type Segment = {a: THREE.Vector3;b: THREE.Vector3;r: number;};
type Pillar = {x: number;z: number;hitY: number;top: number;};

export type SupportResult = {group: THREE.Group;touchpoints: number;height: number;protectedAvoided: number;};

const UP = new THREE.Vector3(0, 1, 0);

function buildInstanced(geometry: THREE.BufferGeometry, segments: Segment[], material: THREE.Material): THREE.InstancedMesh | null {
  if (segments.length === 0) return null;
  const mesh = new THREE.InstancedMesh(geometry, material, segments.length);
  const m = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const s = new THREE.Vector3();
  const dir = new THREE.Vector3();
  const mid = new THREE.Vector3();
  segments.forEach((seg, i) => {
    dir.subVectors(seg.b, seg.a);
    const len = dir.length();
    q.setFromUnitVectors(UP, dir.normalize());
    mid.addVectors(seg.a, seg.b).multiplyScalar(0.5);
    s.set(seg.r, len, seg.r);
    mesh.setMatrixAt(i, m.compose(mid, q, s));
  });
  mesh.instanceMatrix.needsUpdate = true;
  mesh.computeBoundingSphere();
  mesh.computeBoundingBox();
  return mesh;
}

function convexHull(points: [number, number][]): [number, number][] {
  const pts = [...points].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  if (pts.length < 3) return pts;
  const cross = (o: number[], a: number[], b: number[]) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const lower: [number, number][] = [];
  for (const p of pts) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop();
    lower.push(p);
  }
  const upper: [number, number][] = [];
  for (let i = pts.length - 1; i >= 0; i--) {
    const p = pts[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop();
    upper.push(p);
  }
  return lower.slice(0, -1).concat(upper.slice(0, -1));
}

function buildRaft(pillars: Pillar[], material: THREE.Material): THREE.Mesh {
  let hull = convexHull(pillars.map((p) => [p.x, p.z]));
  if (hull.length < 3) {
    const c = pillars[0] ?? { x: 0, z: 0 };
    hull = [
    [c.x - 1, c.z - 1],
    [c.x + 1, c.z - 1],
    [c.x + 1, c.z + 1],
    [c.x - 1, c.z + 1]];

  }
  const cx = hull.reduce((s, p) => s + p[0], 0) / hull.length;
  const cz = hull.reduce((s, p) => s + p[1], 0) / hull.length;
  const shape = new THREE.Shape();
  hull.forEach(([x, z], i) => {
    const dx = x - cx;
    const dz = z - cz;
    const len = Math.hypot(dx, dz) || 1;
    const px = x + dx / len * RAFT_MARGIN;
    const pz = z + dz / len * RAFT_MARGIN;
    if (i === 0) shape.moveTo(px, -pz);else
    shape.lineTo(px, -pz);
  });
  shape.closePath();
  const bevel = 0.35;
  const geometry = new THREE.ExtrudeGeometry(shape, {
    depth: RAFT_THICKNESS - bevel * 2,
    bevelEnabled: true,
    bevelThickness: bevel,
    bevelSize: 0.7,
    bevelSegments: 3
  });
  geometry.rotateX(-Math.PI / 2);
  geometry.translate(0, bevel, 0);
  return new THREE.Mesh(geometry, material);
}

/**
 * Casts a grid of rays up from the platform to find downward-facing surfaces, then
 * builds tapered touchpoints, pillars, X-bracing between neighbours and a raft.
 * Geometry is created in `parent`'s local space so it moves with the model.
 */
export function generateSupports(target: THREE.Object3D, parent: THREE.Object3D, clipPlane: THREE.Plane): SupportResult | null {
  parent.updateMatrixWorld(true);
  const meshes: THREE.Mesh[] = [];
  target.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    if (!mesh.geometry.boundsTree) mesh.geometry.computeBoundsTree();
    meshes.push(mesh);
  });
  if (meshes.length === 0) return null;

  const box = new THREE.Box3().setFromObject(target, true);
  const offset = parent.getWorldPosition(new THREE.Vector3());
  const raycaster = new THREE.Raycaster();
  raycaster.firstHitOnly = true;
  raycaster.far = box.max.y - box.min.y + 6;

  const nx = Math.floor((box.max.x - box.min.x) / SPACING) + 1;
  const nz = Math.floor((box.max.z - box.min.z) / SPACING) + 1;
  const x0 = (box.min.x + box.max.x) / 2 - (nx - 1) * SPACING / 2;
  const z0 = (box.min.z + box.max.z) / 2 - (nz - 1) * SPACING / 2;
  const origin = new THREE.Vector3();
  const normal = new THREE.Vector3();
  const pillars = new Map<string, Pillar>();
  let avoided = 0;

  /** First supportable (downward-facing) surface straight above (x, z). */
  const castUp = (x: number, z: number) => {
    origin.set(x, box.min.y - 3, z);
    raycaster.set(origin, UP);
    const hit = raycaster.intersectObjects(meshes, false)[0];
    if (!hit || !hit.face) return null;
    normal.copy(hit.face.normal).transformDirection(hit.object.matrixWorld);
    if (Math.abs(normal.y) < 0.25) return null;
    return hit;
  };

  const protectAt = (hit: THREE.Intersection) => {
    const protect = (hit.object as THREE.Mesh).geometry.getAttribute('protect');
    if (!protect || !hit.face) return 0;
    return Math.max(protect.getX(hit.face.a), protect.getX(hit.face.b), protect.getX(hit.face.c));
  };

  /** Searches rings around (x, z) for an unprotected surface at roughly the same height. */
  const findUnprotectedNearby = (x: number, z: number, y: number) => {
    for (const radius of [SPACING * 0.35, SPACING * 0.7, SPACING * 1.05]) {
      let best: {x: number;z: number;point: THREE.Vector3;} | null = null;
      let bestDy = Infinity;
      for (let k = 0; k < 10; k++) {
        const a = k / 10 * Math.PI * 2;
        const sx = x + Math.cos(a) * radius;
        const sz = z + Math.sin(a) * radius;
        const h = castUp(sx, sz);
        if (!h || protectAt(h) > 0.3) continue;
        const dy = Math.abs(h.point.y - y);
        if (dy < EDGE_MAX_DY && dy < bestDy) {
          bestDy = dy;
          best = { x: sx, z: sz, point: h.point.clone() };
        }
      }
      if (best) return best;
    }
    return null;
  };

  for (let i = 0; i < nx; i++) {
    for (let j = 0; j < nz; j++) {
      const x = x0 + i * SPACING;
      const z = z0 + j * SPACING;
      origin.set(x, box.min.y - 3, z);
      raycaster.set(origin, UP);
      const hit = castUp(x, z);
      if (!hit) continue;
      let px = x;
      let pz = z;
      let point = hit.point;
      const pv = protectAt(hit);
      if (pv >= 0.9) {
        // Protected: move the touchpoint to the nearest unprotected surface around the
        // painted area so the overhang stays supported from its edges instead.
        const edge = findUnprotectedNearby(x, z, hit.point.y);
        avoided++;
        if (!edge) continue;
        px = edge.x;
        pz = edge.z;
        point = edge.point;
      } else if (pv > 0.3 && (i + j) % 2 === 0) {
        // Soft preference: keep half the touchpoints.
        avoided++;
        continue;
      }
      const top = point.y - BULGE_H - TIP_H;
      if (top < RAFT_THICKNESS + FOOT_H + 1) continue;
      pillars.set(`${i},${j}`, { x: px - offset.x, z: pz - offset.z, hitY: point.y - offset.y, top: top - offset.y });
    }
  }
  if (pillars.size === 0) return null;

  const cylinders: Segment[] = [];
  const feet: Segment[] = [];
  const bulges: Segment[] = [];
  const tips: Segment[] = [];
  const base = RAFT_THICKNESS;

  pillars.forEach((p) => {
    feet.push({ a: new THREE.Vector3(p.x, base, p.z), b: new THREE.Vector3(p.x, base + FOOT_H, p.z), r: 1.1 });
    cylinders.push({ a: new THREE.Vector3(p.x, base + FOOT_H, p.z), b: new THREE.Vector3(p.x, p.top, p.z), r: PILLAR_R });
    bulges.push({ a: new THREE.Vector3(p.x, p.top, p.z), b: new THREE.Vector3(p.x, p.top + BULGE_H, p.z), r: 0.7 });
    tips.push({ a: new THREE.Vector3(p.x, p.top + BULGE_H, p.z), b: new THREE.Vector3(p.x, p.hitY, p.z), r: 0.7 });
  });

  // X-bracing between grid neighbours gives the lattice look.
  pillars.forEach((p, key) => {
    const [i, j] = key.split(',').map(Number);
    for (const n of [pillars.get(`${i + 1},${j}`), pillars.get(`${i},${j + 1}`)]) {
      if (!n) continue;
      const ceiling = Math.min(p.top, n.top) - 0.6;
      for (let y = base + FOOT_H + 0.8; y + SPACING <= ceiling; y += SPACING) {
        cylinders.push({ a: new THREE.Vector3(p.x, y, p.z), b: new THREE.Vector3(n.x, y + SPACING, n.z), r: BRACE_R });
        cylinders.push({ a: new THREE.Vector3(n.x, y, n.z), b: new THREE.Vector3(p.x, y + SPACING, p.z), r: BRACE_R });
      }
    }
  });

  const material = new THREE.MeshStandardMaterial({ color: '#c8c9cc', roughness: 0.45, metalness: 0.05, clippingPlanes: [clipPlane] });
  const group = new THREE.Group();
  group.name = 'supports';
  [
  buildInstanced(new THREE.CylinderGeometry(1, 1, 1, 8), cylinders, material),
  buildInstanced(new THREE.CylinderGeometry(0.38, 1, 1, 10), feet, material),
  buildInstanced(new THREE.CylinderGeometry(1, 0.6, 1, 8), bulges, material),
  buildInstanced(new THREE.CylinderGeometry(0.2, 1, 1, 8), tips, material)].
  forEach((m) => m && group.add(m));
  group.add(buildRaft([...pillars.values()], material));

  const height = Math.max(...[...pillars.values()].map((p) => p.hitY));
  return { group, touchpoints: pillars.size, height, protectedAvoided: avoided };
}

export function disposeSupports(group: THREE.Group) {
  const materials = new Set<THREE.Material>();
  group.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    mesh.geometry.dispose();
    materials.add(mesh.material as THREE.Material);
  });
  materials.forEach((m) => m.dispose());
}
